/**
 * Regions Redux Slice
 * State management for regions domain
 */

import { createSlice, type PayloadAction } from '@ufc/core'

import type {
  Region,
  RegionListItem,
  RegionStats,
  RegionsFilter,
  PaginationMeta,
  CreateRegionRequest,
  UpdateRegionRequest,
  FactorySummary,
  CountrySummary,
} from '../domain/types'
import { DEFAULT_REGION_FILTER, DEFAULT_REGION_STATS } from '../domain/constants'

// ===========================================
// State Interface
// ===========================================

export interface RegionsState {
  items: RegionListItem[]
  selectedRegion: Region | null
  stats: RegionStats
  filter: RegionsFilter
  pagination: PaginationMeta
  // Reference data
  availableFactories: FactorySummary[]
  availableCountries: CountrySummary[]
  // UI state
  loading: boolean
  detailLoading: boolean
  error: string | null
}

// ===========================================
// Initial State
// ===========================================

const initialState: RegionsState = {
  items: [],
  selectedRegion: null,
  stats: DEFAULT_REGION_STATS,
  filter: DEFAULT_REGION_FILTER,
  pagination: {
    page: 1,
    pageSize: 20,
    pageCount: 1,
    total: 0,
  },
  availableFactories: [],
  availableCountries: [],
  loading: false,
  detailLoading: false,
  error: null,
}

// ===========================================
// Slice
// ===========================================

export const regionsSlice = createSlice({
  name: 'regions',
  initialState,
  reducers: {
    // ==========================================
    // Data Setters
    // ==========================================

    setRegions: (state, action: PayloadAction<RegionListItem[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },

    setPagination: (state, action: PayloadAction<PaginationMeta>) => {
      state.pagination = action.payload
    },

    setRegionStats: (state, action: PayloadAction<RegionStats>) => {
      state.stats = action.payload
    },

    setSelectedRegion: (state, action: PayloadAction<Region | null>) => {
      state.selectedRegion = action.payload
      state.detailLoading = false
    },

    setFilter: (state, action: PayloadAction<Partial<RegionsFilter>>) => {
      state.filter = { ...state.filter, ...action.payload }
    },

    setAvailableFactories: (state, action: PayloadAction<FactorySummary[]>) => {
      state.availableFactories = action.payload
    },

    // ==========================================
    // Fetch Actions (saga triggers)
    // ==========================================

    fetchRegions: (state, _action: PayloadAction<RegionsFilter | undefined>) => {
      state.loading = true
      state.error = null
    },

    fetchRegionsSuccess: (
      state,
      action: PayloadAction<{ data: RegionListItem[]; pagination: PaginationMeta }>
    ) => {
      state.items = action.payload.data
      state.pagination = action.payload.pagination
      state.loading = false
    },

    fetchRegionsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    fetchRegionDetail: (state, _action: PayloadAction<string>) => {
      state.detailLoading = true
      state.error = null
    },

    fetchRegionDetailSuccess: (state, action: PayloadAction<Region>) => {
      state.selectedRegion = action.payload
      state.detailLoading = false
    },

    fetchRegionDetailFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.detailLoading = false
    },

    fetchRegionStats: (state) => {
      // No loading state change for stats
    },

    fetchRegionStatsSuccess: (state, action: PayloadAction<RegionStats>) => {
      state.stats = action.payload
    },

    fetchAvailableFactories: (state) => {
      // Triggered to fetch factories for form
    },

    fetchAvailableFactoriesSuccess: (state, action: PayloadAction<FactorySummary[]>) => {
      state.availableFactories = action.payload
    },

    fetchAvailableCountries: (state) => {
      // Triggered to fetch countries for form
    },

    fetchAvailableCountriesSuccess: (state, action: PayloadAction<CountrySummary[]>) => {
      state.availableCountries = action.payload
    },

    // ==========================================
    // CRUD Actions (saga triggers)
    // ==========================================

    createRegion: (state, _action: PayloadAction<CreateRegionRequest>) => {
      state.loading = true
      state.error = null
    },

    createRegionSuccess: (state, action: PayloadAction<Region>) => {
      // Prepend to items list as list item
      const newItem: RegionListItem = {
        id: action.payload.id,
        documentId: action.payload.documentId,
        code: action.payload.code,
        name: action.payload.name,
        description: action.payload.description,
        isActive: action.payload.isActive,
        countryId: action.payload.country?.id,
        countryCode: action.payload.country?.code,
        countryName: action.payload.country?.name,
        factoryCount: action.payload.factories?.length ?? 0,
        factoryNames: action.payload.factories?.map((f) => f.name),
        cityCount: action.payload.cities?.length ?? 0,
        createdAt: action.payload.createdAt,
        updatedAt: action.payload.updatedAt,
      }
      state.items.unshift(newItem)
      state.loading = false
    },

    createRegionFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    updateRegion: (
      state,
      _action: PayloadAction<{ documentId: string; data: UpdateRegionRequest }>
    ) => {
      state.loading = true
      state.error = null
    },

    updateRegionSuccess: (state, action: PayloadAction<Region>) => {
      const index = state.items.findIndex(
        (item) => item.documentId === action.payload.documentId
      )
      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          name: action.payload.name,
          description: action.payload.description,
          isActive: action.payload.isActive,
          countryId: action.payload.country?.id,
          countryCode: action.payload.country?.code,
          countryName: action.payload.country?.name,
          factoryCount: action.payload.factories?.length ?? 0,
          factoryNames: action.payload.factories?.map((f) => f.name),
          cityCount: action.payload.cities?.length ?? 0,
          updatedAt: action.payload.updatedAt,
        }
      }
      if (state.selectedRegion?.documentId === action.payload.documentId) {
        state.selectedRegion = action.payload
      }
      state.loading = false
    },

    updateRegionFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    deleteRegion: (state, _action: PayloadAction<string>) => {
      state.loading = true
      state.error = null
    },

    deleteRegionSuccess: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.documentId !== action.payload)
      if (state.selectedRegion?.documentId === action.payload) {
        state.selectedRegion = null
      }
      state.loading = false
    },

    deleteRegionFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // ==========================================
    // Error Handling
    // ==========================================

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    clearError: (state) => {
      state.error = null
    },

    // ==========================================
    // Reset
    // ==========================================

    reset: () => initialState,

    clearSelectedRegion: (state) => {
      state.selectedRegion = null
    },
  },
})

// ===========================================
// Exports
// ===========================================

export const regionsActions = regionsSlice.actions
export const regionsReducer = regionsSlice.reducer
